import React, { useMemo } from 'react';

const PathwayDiagram = ({ pathway, mutations, pathwayData }) => {
  const selectedPathway = pathwayData[pathway];
  if (!selectedPathway) return <div className="p-4 text-gray-500">Pathway not found</div>;

  // Calculate mutation counts per gene
  const geneCounts = useMemo(() => {
    const counts = {};
    selectedPathway.genes.forEach(gene => {
      counts[gene] = mutations.filter(m => m.gene === gene).length;
    });
    return counts;
  }, [mutations, selectedPathway.genes]);

  // Find max count for color scaling
  const maxCount = Math.max(...Object.values(geneCounts), 1);

  // Color based on mutation count
  const getColor = (count) => {
    if (count === 0) return '#ecf0f1';
    const intensity = Math.min(count / maxCount, 1);
    // Red gradient
    const r = Math.round(231 - (231 - 192) * (1 - intensity));
    const g = Math.round(76 - 76 * intensity);
    const b = Math.round(60 - 60 * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Pathway-specific layouts
  const layouts = {
    RTK_RAS: [
      { gene: 'EGFR', x: 50, y: 50 },
      { gene: 'ERBB2', x: 150, y: 50 },
      { gene: 'MET', x: 250, y: 50 },
      { gene: 'ALK', x: 350, y: 50 },
      { gene: 'RET', x: 450, y: 50 },
      { gene: 'ROS1', x: 550, y: 50 },
      { gene: 'FGFR1', x: 650, y: 50 },
      { gene: 'FGFR2', x: 100, y: 130 },
      { gene: 'FGFR3', x: 200, y: 130 },
      { gene: 'KIT', x: 300, y: 130 },
      { gene: 'PDGFRA', x: 400, y: 130 },
      { gene: 'NF1', x: 550, y: 130 },
      { gene: 'KRAS', x: 200, y: 210 },
      { gene: 'NRAS', x: 300, y: 210 },
      { gene: 'HRAS', x: 400, y: 210 },
      { gene: 'BRAF', x: 300, y: 290 },
      { gene: 'MAP2K1', x: 300, y: 370 }
    ],
    PI3K_AKT: [
      { gene: 'PIK3CA', x: 150, y: 50 },
      { gene: 'PIK3R1', x: 250, y: 50 },
      { gene: 'PTEN', x: 350, y: 100 },
      { gene: 'AKT1', x: 200, y: 150 },
      { gene: 'TSC1', x: 150, y: 250 },
      { gene: 'TSC2', x: 250, y: 250 },
      { gene: 'STK11', x: 350, y: 200 }
    ],
    DNA_REPAIR: [
      { gene: 'BRCA1', x: 100, y: 50 },
      { gene: 'BRCA2', x: 200, y: 50 },
      { gene: 'PALB2', x: 150, y: 130 },
      { gene: 'RAD51C', x: 50, y: 130 },
      { gene: 'RAD51D', x: 250, y: 130 },
      { gene: 'ATM', x: 400, y: 50 },
      { gene: 'ATR', x: 500, y: 50 },
      { gene: 'CHEK2', x: 450, y: 130 },
      { gene: 'MLH1', x: 100, y: 220 },
      { gene: 'MSH2', x: 200, y: 220 },
      { gene: 'MSH6', x: 300, y: 220 },
      { gene: 'PMS2', x: 400, y: 220 },
      { gene: 'POLE', x: 500, y: 220 },
      { gene: 'MUTYH', x: 600, y: 220 }
    ],
    CHROMATIN: [
      { gene: 'ARID1A', x: 100, y: 50 },
      { gene: 'ARID2', x: 200, y: 50 },
      { gene: 'SMARCA4', x: 300, y: 50 },
      { gene: 'SMARCB1', x: 400, y: 50 },
      { gene: 'KDM6A', x: 150, y: 130 },
      { gene: 'EZH2', x: 250, y: 130 },
      { gene: 'CREBBP', x: 350, y: 130 },
      { gene: 'EP300', x: 450, y: 130 },
      { gene: 'DNMT3A', x: 200, y: 210 },
      { gene: 'H3F3A', x: 300, y: 210 }
    ],
    TP53: [
      { gene: 'TP53', x: 300, y: 150 },
      { gene: 'MDM2', x: 450, y: 150 },
      { gene: 'ATM', x: 150, y: 50 },
      { gene: 'ATR', x: 300, y: 50 },
      { gene: 'CHEK2', x: 450, y: 50 }
    ],
    CELL_CYCLE: [
      { gene: 'RB1', x: 200, y: 50 },
      { gene: 'CDKN2A', x: 350, y: 50 },
      { gene: 'CDK4', x: 150, y: 150 },
      { gene: 'CDK6', x: 250, y: 150 },
      { gene: 'CCND1', x: 350, y: 150 }
    ],
    WNT: [
      { gene: 'APC', x: 200, y: 50 },
      { gene: 'CTNNB1', x: 200, y: 150 },
      { gene: 'RNF43', x: 350, y: 100 }
    ],
    NOTCH: [
      { gene: 'NOTCH1', x: 200, y: 50 },
      { gene: 'FBXW7', x: 350, y: 100 }
    ],
    SPLICING: [
      { gene: 'SF3B1', x: 150, y: 50 },
      { gene: 'SRSF2', x: 300, y: 50 },
      { gene: 'U2AF1', x: 450, y: 50 }
    ],
    JAK_STAT: [
      { gene: 'JAK2', x: 200, y: 50 },
      { gene: 'STAT3', x: 200, y: 150 },
      { gene: 'SH2B3', x: 350, y: 50 },
      { gene: 'SOCS1', x: 350, y: 150 }
    ]
  };

  const layout = layouts[pathway] || selectedPathway.genes.map((gene, i) => ({
    gene,
    x: 100 + (i % 5) * 120,
    y: 50 + Math.floor(i / 5) * 100
  }));

  // Calculate SVG dimensions based on layout
  const maxX = Math.max(...layout.map(n => n.x)) + 100;
  const maxY = Math.max(...layout.map(n => n.y)) + 80;

  // Define connections between genes
  const connections = {
    RTK_RAS: [
      ['EGFR', 'KRAS'], ['ERBB2', 'KRAS'], ['MET', 'KRAS'], ['ALK', 'KRAS'],
      ['RET', 'KRAS'], ['FGFR1', 'KRAS'], ['FGFR2', 'KRAS'], ['FGFR3', 'KRAS'],
      ['KIT', 'KRAS'], ['PDGFRA', 'KRAS'], ['NF1', 'KRAS'],
      ['KRAS', 'BRAF'], ['NRAS', 'BRAF'], ['HRAS', 'BRAF'],
      ['BRAF', 'MAP2K1']
    ],
    PI3K_AKT: [
      ['PIK3CA', 'AKT1'], ['PIK3R1', 'PIK3CA'], ['PTEN', 'AKT1'],
      ['AKT1', 'TSC1'], ['AKT1', 'TSC2'], ['STK11', 'TSC2']
    ],
    DNA_REPAIR: [
      ['BRCA1', 'PALB2'], ['BRCA2', 'PALB2'], ['RAD51C', 'BRCA2'], ['RAD51D', 'BRCA2'],
      ['ATM', 'CHEK2'], ['ATR', 'CHEK2'],
      ['MLH1', 'MSH2'], ['MSH2', 'MSH6'], ['MLH1', 'PMS2']
    ],
    CHROMATIN: [
      ['ARID1A', 'SMARCA4'], ['ARID2', 'SMARCA4'], ['SMARCB1', 'SMARCA4'],
      ['KDM6A', 'EZH2'], ['CREBBP', 'EP300']
    ],
    TP53: [
      ['ATM', 'TP53'], ['ATR', 'TP53'], ['CHEK2', 'TP53'], ['MDM2', 'TP53']
    ],
    CELL_CYCLE: [
      ['CDKN2A', 'CDK4'], ['CDKN2A', 'CDK6'], ['CDK4', 'RB1'], ['CDK6', 'RB1'],
      ['CCND1', 'CDK4'], ['CCND1', 'CDK6']
    ],
    WNT: [
      ['APC', 'CTNNB1'], ['RNF43', 'CTNNB1']
    ],
    NOTCH: [
      ['FBXW7', 'NOTCH1']
    ],
    JAK_STAT: [
      ['JAK2', 'STAT3'], ['SH2B3', 'JAK2'], ['SOCS1', 'JAK2']
    ]
  };

  const pathwayConnections = connections[pathway] || [];

  // Find node position by gene name
  const getNodePos = (gene) => {
    const node = layout.find(n => n.gene === gene);
    return node ? { x: node.x + 40, y: node.y + 15 } : null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-2">{selectedPathway.name}</h3>
      <p className="text-sm text-gray-600 mb-4">{selectedPathway.description}</p>

      <svg width={maxX} height={maxY} className="overflow-visible">
        {/* Connections */}
        {pathwayConnections.map(([from, to], i) => {
          const fromPos = getNodePos(from);
          const toPos = getNodePos(to);
          if (!fromPos || !toPos) return null;
          return (
            <line
              key={i}
              x1={fromPos.x}
              y1={fromPos.y}
              x2={toPos.x}
              y2={toPos.y}
              stroke="#bdc3c7"
              strokeWidth={2}
              markerEnd="url(#arrowhead)"
            />
          );
        })}

        {/* Arrowhead marker */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#bdc3c7" />
          </marker>
        </defs>

        {/* Gene nodes */}
        {layout.filter(node => selectedPathway.genes.includes(node.gene)).map(node => {
          const count = geneCounts[node.gene] || 0;
          return (
            <g key={node.gene} className="cursor-pointer hover:opacity-80">
              <rect
                x={node.x}
                y={node.y}
                width={80}
                height={30}
                rx={5}
                fill={getColor(count)}
                stroke={count > 0 ? '#c0392b' : '#bdc3c7'}
                strokeWidth={count > 0 ? 2 : 1}
              />
              <text
                x={node.x + 40}
                y={node.y + 15}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={count > maxCount * 0.5 ? 'white' : '#2c3e50'}
                fontSize="11"
                fontWeight="bold"
              >
                {node.gene}
              </text>
              <text
                x={node.x + 40}
                y={node.y + 45}
                textAnchor="middle"
                fill="#7f8c8d"
                fontSize="10"
              >
                {count} mutations
              </text>
              <title>{node.gene}: {count} mutations in database</title>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 text-sm">
        <span className="font-semibold">Mutation Count:</span>
        <div className="flex items-center gap-1">
          <div className="w-6 h-4 rounded" style={{ backgroundColor: '#ecf0f1' }}></div>
          <span>0</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 h-4 rounded" style={{ backgroundColor: getColor(maxCount * 0.25) }}></div>
          <span>Low</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 h-4 rounded" style={{ backgroundColor: getColor(maxCount * 0.5) }}></div>
          <span>Medium</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 h-4 rounded" style={{ backgroundColor: getColor(maxCount) }}></div>
          <span>High</span>
        </div>
      </div>
    </div>
  );
};

export default PathwayDiagram;
